import { useEffect, useRef } from 'react';
import Hero from './components/Hero';
import Services from './components/Services';
import HowItWorks from './components/HowItWorks';
import Benefits from './components/Benefits';
import DemoExperience from './components/DemoExperience';
import WhyBayfront from './components/WhyBayfront';
import Pricing from './components/Pricing';
import FinalCTA from './components/FinalCTA';
import BookingCalendar from './components/BookingCalendar';
import Footer from './components/Footer';

export default function Home() {
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

    const schemaOrg = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'Organization',
          '@id': `${siteUrl}/#organization`,
          name: 'Bayfront Intelligence',
          url: siteUrl,
          description:
            'Bayfront Intelligence helps home service businesses automate lead capture, booking, and follow-up with AI voice agents, smart calendars, and automated systems.',
          sameAs: [],
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer support',
            availableLanguage: 'English',
          },
        },
        {
          '@type': 'WebSite',
          '@id': `${siteUrl}/#website`,
          url: siteUrl,
          name: 'Bayfront Intelligence',
          publisher: { '@id': `${siteUrl}/#organization` },
        },
        {
          '@type': 'WebPage',
          '@id': `${siteUrl}/#webpage`,
          url: siteUrl,
          name: 'Bayfront Intelligence | AI Automation for Home Service Businesses',
          description:
            'Bayfront Intelligence helps plumbers, electricians, HVAC, landscapers, and contractors automate lead capture, booking, and follow-up with AI voice agents and smart systems.',
          isPartOf: { '@id': `${siteUrl}/#website` },
          about: { '@id': `${siteUrl}/#organization` },
          inLanguage: 'en-US',
        },
        {
          '@type': 'Service',
          '@id': `${siteUrl}/#service`,
          name: 'AI Automation for Home Service Businesses',
          provider: { '@id': `${siteUrl}/#organization` },
          serviceType: 'AI Business Automation',
          description:
            'AI voice agents, smart calendars, website design, lead capture systems, automated follow-up, and CRM automation built for home service professionals.',
          audience: {
            '@type': 'Audience',
            audienceType:
              'Plumbers, Electricians, HVAC Companies, Landscapers, Painters, Contractors',
          },
          hasOfferCatalog: {
            '@type': 'OfferCatalog',
            name: 'AI Automation Services',
            itemListElement: [
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'AI Voice Agents' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Smart Calendars' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Website Design' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Lead Capture Systems' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Automated Follow-Up' } },
              { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'CRM & Client Communication Automation' } },
            ],
          },
        },
        {
          '@type': 'FAQPage',
          '@id': `${siteUrl}/#faq`,
          mainEntity: [
            {
              '@type': 'Question',
              name: 'What is Bayfront Intelligence?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Bayfront Intelligence is an AI automation company that helps home service businesses generate leads, book jobs, automate follow-up, and save time using AI voice agents and smart systems.',
              },
            },
            {
              '@type': 'Question',
              name: 'Who is Bayfront Intelligence built for?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Bayfront Intelligence is built for home service businesses including plumbers, electricians, HVAC companies, landscapers, painters, and general contractors.',
              },
            },
            {
              '@type': 'Question',
              name: 'What services does Bayfront Intelligence offer?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Services include AI Voice Agents, Smart Calendars, Website Design, Lead Capture Systems, Automated Follow-Up, and CRM & Client Communication Automation.',
              },
            },
            {
              '@type': 'Question',
              name: 'How does the AI automation work?',
              acceptedAnswer: {
                '@type': 'Answer',
                text: 'Bayfront Intelligence builds your automation system, connects your lead sources, deploys AI to handle follow-up 24/7, and ensures more jobs get booked without manual effort.',
              },
            },
          ],
        },
      ],
    };

    const existingScript = document.getElementById('schema-org-jsonld');
    if (existingScript) existingScript.remove();

    const script = document.createElement('script');
    script.id = 'schema-org-jsonld';
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(schemaOrg);
    document.head.appendChild(script);

    return () => {
      const s = document.getElementById('schema-org-jsonld');
      if (s) s.remove();
    };
  }, []);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.scroll-reveal');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="bg-black overflow-hidden">
      <Hero />
      <Services />
      <HowItWorks />
      <Benefits />
      <DemoExperience />
      <WhyBayfront />
      <Pricing />
      <FinalCTA />
      <BookingCalendar />
      <Footer />
    </div>
  );
}
